import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Moon, ArrowLeft, Upload, Save, Download, UploadCloud, RefreshCw, ChevronDown, X, Crown, UserCheck, UserX, CreditCard, BookOpen, Plus, Trash2, Pencil, BarChart3, RotateCcw, Mail, Send, Eye, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  adminAuth,
  fetchNakshatras,
  updateNakshatra,
  uploadNakshatraImage,
  exportAllData,
  importData,
  seedDatabase,
  adminGrantPremium,
  adminRevokePremium,
  adminListPremiumUsers,
  adminResetMonthlyReports,
  adminListPayments,
  adminDigestPreview,
  adminDigestSend,
  adminDigestSendTest,
  adminDigestLogs,
  fetchKBCategories,
  fetchKBCategory,
  adminCreateKBArticle,
  adminUpdateKBArticle,
  adminDeleteKBArticle,
  adminCreateKBCategory,
  adminUpdateKBCategory,
  adminDeleteKBCategory,
  adminSeedKB,
} from "@/lib/api";
import { toast } from "sonner";
import AnalyticsDashboard from "@/pages/AnalyticsDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function Admin() {
  const [passcode, setPasscode] = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [nakshatras, setNakshatras] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await adminAuth(passcode);
      setAuthenticated(true);
      toast.success("Authenticated");
    } catch {
      toast.error("Invalid passcode");
    }
  };

  const loadNakshatras = useCallback(async () => {
    try {
      const data = await fetchNakshatras();
      setNakshatras(data);
    } catch {
      toast.error("Failed to load nakshatras");
    }
  }, []);

  useEffect(() => {
    if (authenticated) loadNakshatras();
  }, [authenticated, loadNakshatras]);

  const handleExport = async () => {
    try {
      const data = await exportAllData(passcode);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `daily_nakshatra_export_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Exported successfully");
    } catch {
      toast.error("Export failed");
    }
  };

  const handleImport = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        const data = JSON.parse(text);
        await importData(data, passcode);
        toast.success("Imported successfully");
        loadNakshatras();
      } catch {
        toast.error("Import failed");
      }
    };
    input.click();
  };

  const handleSeed = async () => {
    if (!window.confirm("This will reset all nakshatra data to defaults. Continue?")) return;
    try {
      await seedDatabase(passcode);
      toast.success("Database seeded with defaults");
      loadNakshatras();
    } catch {
      toast.error("Seed failed");
    }
  };

  // Passcode Gate
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-full max-w-sm px-6">
          <div className="mb-8 text-center">
            <Moon className="w-8 h-8 text-white/30 mx-auto mb-4" />
            <h1 className="text-3xl font-light text-white/90 mb-2">Admin Access</h1>
            <p className="text-white/30 text-sm">Enter passcode to continue</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6" data-testid="admin-login-form">
            <Input
              type="password"
              placeholder="Passcode"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              className="bg-transparent border-0 border-b border-white/20 rounded-none text-white/90 text-center text-lg tracking-[0.3em] focus:border-white/60 focus-visible:ring-0 focus-visible:ring-offset-0 h-14 placeholder:text-white/15"
              data-testid="admin-passcode-input"
            />
            <Button
              type="submit"
              className="w-full bg-white text-black hover:bg-white/90 rounded-full h-12 text-sm tracking-wider"
              data-testid="admin-login-btn"
            >
              Enter
            </Button>
          </form>
          <div className="mt-6 text-center">
            <Link to="/" className="text-white/30 text-sm hover:text-white/50">Back to Dashboard</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-white/90" data-testid="admin-back-link">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm tracking-wider uppercase">Dashboard</span>
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={handleExport} className="text-white/50 hover:text-white hover:bg-white/5 text-xs" data-testid="admin-export-btn">
              <Download className="w-3.5 h-3.5 mr-1" /> Export
            </Button>
            <Button variant="ghost" size="sm" onClick={handleImport} className="text-white/50 hover:text-white hover:bg-white/5 text-xs" data-testid="admin-import-btn">
              <UploadCloud className="w-3.5 h-3.5 mr-1" /> Import
            </Button>
            <Button variant="ghost" size="sm" onClick={handleSeed} className="text-white/50 hover:text-white hover:bg-white/5 text-xs" data-testid="admin-seed-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Seed
            </Button>
          </div>
        </div>
      </nav>

      <main className="pt-14 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-4xl font-light text-white/95 mb-8" data-testid="admin-title">Admin Dashboard</h1>

        <Tabs defaultValue="content" className="w-full">
          <TabsList className="bg-white/5 border border-white/10 mb-6">
            <TabsTrigger value="content" className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/50" data-testid="tab-content">
              <Moon className="w-4 h-4 mr-2" />
              Content Management
            </TabsTrigger>
            <TabsTrigger value="kb" className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/50" data-testid="tab-kb">
              <BookOpen className="w-4 h-4 mr-2" />
              Knowledge Base
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/50" data-testid="tab-analytics">
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="premium" className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/50" data-testid="tab-premium">
              <Crown className="w-4 h-4 mr-2" />
              Premium Testing
            </TabsTrigger>
            <TabsTrigger value="digest" className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/50" data-testid="tab-digest">
              <Mail className="w-4 h-4 mr-2" />
              Weekly Digest
            </TabsTrigger>
          </TabsList>

          <TabsContent value="content">
            {/* Nakshatra List */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-nakshatra-grid">
              {nakshatras.map((nak) => (
                <button
                  key={nak.slug}
                  onClick={() => setEditing(nak)}
                  className="text-left rounded-lg p-4 transition-colors duration-200 hover:bg-white/[0.06]"
                  style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
                  data-testid={`admin-nak-${nak.slug}`}
                >
                  <div className="flex items-center gap-3">
                    {nak.featured_image_url ? (
                      <img
                        src={nak.featured_image_url.startsWith("http") ? nak.featured_image_url : nak.featured_image_url}
                        alt={nak.name}
                        className="w-10 h-12 object-cover rounded"
                      />
                    ) : (
                      <div className="w-10 h-12 rounded bg-white/5 flex items-center justify-center">
                        <Moon className="w-4 h-4 text-white/20" />
                      </div>
                    )}
                    <div>
                      <p className="text-white/80 text-sm font-medium">{nak.index}. {nak.name}</p>
                      <p className="text-white/30 text-xs">{nak.planetary_ruler} / {nak.deity?.split("(")[0]?.trim()}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="kb">
            <KBManagementPanel passcode={passcode} />
          </TabsContent>

          <TabsContent value="analytics">
            <AnalyticsDashboard passcode={passcode} />
          </TabsContent>

          <TabsContent value="premium">
            <PremiumTestingPanel passcode={passcode} />
          </TabsContent>

          <TabsContent value="digest">
            <DigestPanel passcode={passcode} />
          </TabsContent>
        </Tabs>
      </main>

      {/* Edit Dialog */}
      {editing && (
        <NakshatraEditor
          nakshatra={editing}
          passcode={passcode}
          onClose={() => setEditing(null)}
          onSaved={() => {
            loadNakshatras();
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function NakshatraEditor({ nakshatra, passcode, onClose, onSaved }) {
  const [form, setForm] = useState({
    deity: nakshatra.deity || "",
    symbolism: nakshatra.symbolism || "",
    planetary_ruler: nakshatra.planetary_ruler || "",
    birth_traits: nakshatra.birth_traits || "",
    mythology: nakshatra.mythology || "",
    pada_1: nakshatra.pada_1 || "",
    pada_2: nakshatra.pada_2 || "",
    pada_3: nakshatra.pada_3 || "",
    pada_4: nakshatra.pada_4 || "",
    lunar_climate_pointers: nakshatra.lunar_climate_pointers || [],
  });
  const [saving, setSaving] = useState(false);
  const [newPointer, setNewPointer] = useState("");

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateNakshatra(nakshatra.slug, form, passcode);
      toast.success(`${nakshatra.name} updated`);
      onSaved();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const result = await uploadNakshatraImage(nakshatra.slug, file, passcode);
      toast.success("Image uploaded");
      onSaved();
    } catch {
      toast.error("Image upload failed");
    }
  };

  const addPointer = () => {
    if (!newPointer.trim()) return;
    handleChange("lunar_climate_pointers", [...form.lunar_climate_pointers, newPointer.trim()]);
    setNewPointer("");
  };

  const removePointer = (index) => {
    handleChange("lunar_climate_pointers", form.lunar_climate_pointers.filter((_, i) => i !== index));
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl bg-[#0A0A0A] border-white/10 text-white max-h-[90vh] overflow-hidden p-0" data-testid="admin-editor-dialog">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-white/5">
          <DialogTitle className="text-2xl font-light font-[Cormorant_Garamond] text-white/90">
            {nakshatra.index}. {nakshatra.name}
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[calc(90vh-140px)] px-6 py-4">
          <div className="space-y-6 pb-6">
            {/* Image Upload */}
            <div>
              <Label className="text-white/50 text-xs tracking-[0.15em] uppercase mb-2 block">Featured Image (4:5)</Label>
              <div className="flex items-center gap-4">
                {nakshatra.featured_image_url ? (
                  <img
                    src={nakshatra.featured_image_url.startsWith("http") ? nakshatra.featured_image_url : nakshatra.featured_image_url}
                    alt={nakshatra.name}
                    className="w-20 h-24 object-cover rounded"
                  />
                ) : (
                  <div className="w-20 h-24 rounded bg-white/5 flex items-center justify-center">
                    <Moon className="w-6 h-6 text-white/20" />
                  </div>
                )}
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} data-testid="admin-image-upload" />
                  <span className="flex items-center gap-2 text-sm text-white/50 hover:text-white/80 border border-white/10 rounded-full px-4 py-2">
                    <Upload className="w-4 h-4" /> Upload
                  </span>
                </label>
              </div>
            </div>

            {/* Basic Info */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-white/50 text-xs tracking-[0.15em] uppercase mb-1 block">Deity</Label>
                <Input
                  value={form.deity}
                  onChange={(e) => handleChange("deity", e.target.value)}
                  className="bg-white/5 border-white/10 text-white/90"
                  data-testid="admin-deity-input"
                />
              </div>
              <div>
                <Label className="text-white/50 text-xs tracking-[0.15em] uppercase mb-1 block">Symbolism</Label>
                <Input
                  value={form.symbolism}
                  onChange={(e) => handleChange("symbolism", e.target.value)}
                  className="bg-white/5 border-white/10 text-white/90"
                  data-testid="admin-symbolism-input"
                />
              </div>
              <div>
                <Label className="text-white/50 text-xs tracking-[0.15em] uppercase mb-1 block">Planetary Ruler</Label>
                <Input
                  value={form.planetary_ruler}
                  onChange={(e) => handleChange("planetary_ruler", e.target.value)}
                  className="bg-white/5 border-white/10 text-white/90"
                  data-testid="admin-ruler-input"
                />
              </div>
            </div>

            {/* Birth Traits */}
            <div>
              <Label className="text-white/50 text-xs tracking-[0.15em] uppercase mb-1 block">Birth Traits (Markdown)</Label>
              <Textarea
                value={form.birth_traits}
                onChange={(e) => handleChange("birth_traits", e.target.value)}
                rows={5}
                className="bg-white/5 border-white/10 text-white/80 font-mono text-sm"
                data-testid="admin-traits-textarea"
              />
            </div>

            {/* Mythology */}
            <div>
              <Label className="text-white/50 text-xs tracking-[0.15em] uppercase mb-1 block">Mythology (Markdown)</Label>
              <Textarea
                value={form.mythology}
                onChange={(e) => handleChange("mythology", e.target.value)}
                rows={5}
                className="bg-white/5 border-white/10 text-white/80 font-mono text-sm"
                data-testid="admin-mythology-textarea"
              />
            </div>

            {/* Padas */}
            {[1, 2, 3, 4].map((num) => (
              <div key={num}>
                <Label className="text-white/50 text-xs tracking-[0.15em] uppercase mb-1 block">Pada {num} (Markdown)</Label>
                <Textarea
                  value={form[`pada_${num}`]}
                  onChange={(e) => handleChange(`pada_${num}`, e.target.value)}
                  rows={3}
                  className="bg-white/5 border-white/10 text-white/80 font-mono text-sm"
                  data-testid={`admin-pada-${num}-textarea`}
                />
              </div>
            ))}

            {/* Lunar Climate Pointers */}
            <div>
              <Label className="text-white/50 text-xs tracking-[0.15em] uppercase mb-2 block">Lunar Climate Pointers</Label>
              <div className="space-y-2 mb-3">
                {form.lunar_climate_pointers.map((p, i) => (
                  <div key={i} className="flex items-center gap-2 bg-white/5 rounded px-3 py-2">
                    <span className="text-white/60 text-sm flex-1">{p}</span>
                    <button onClick={() => removePointer(i)} className="text-white/30 hover:text-red-400">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newPointer}
                  onChange={(e) => setNewPointer(e.target.value)}
                  placeholder="Add guidance pointer..."
                  className="bg-white/5 border-white/10 text-white/80 text-sm flex-1"
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addPointer())}
                  data-testid="admin-pointer-input"
                />
                <Button variant="ghost" size="sm" onClick={addPointer} className="text-white/50 hover:text-white border border-white/10" data-testid="admin-add-pointer-btn">
                  Add
                </Button>
              </div>
            </div>
          </div>
        </ScrollArea>

        {/* Save Button */}
        <div className="px-6 py-4 border-t border-white/5 flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose} className="text-white/50 hover:text-white" data-testid="admin-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-white text-black hover:bg-white/90 rounded-full px-6"
            data-testid="admin-save-btn"
          >
            {saving ? "Saving..." : "Save Changes"}
            <Save className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PremiumTestingPanel({ passcode }) {
  const [email, setEmail] = useState("");
  const [premiumUsers, setPremiumUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [granting, setGranting] = useState(false);
  const [resetting, setResetting] = useState(false);

  const loadPremiumUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const data = await adminListPremiumUsers(passcode);
      setPremiumUsers(data.premium_users || []);
    } catch {
      toast.error("Failed to load premium users");
    } finally {
      setLoadingUsers(false);
    }
  }, [passcode]);

  const loadPayments = useCallback(async () => {
    setLoadingPayments(true);
    try {
      const data = await adminListPayments(passcode);
      setPayments(data.transactions || []);
    } catch {
      toast.error("Failed to load payments");
    } finally {
      setLoadingPayments(false);
    }
  }, [passcode]);

  useEffect(() => {
    loadPremiumUsers();
    loadPayments();
  }, [loadPremiumUsers, loadPayments]);

  const handleGrantPremium = async () => {
    if (!email.trim()) return;
    setGranting(true);
    try {
      await adminGrantPremium(email.trim(), passcode);
      toast.success(`Premium granted to ${email}`);
      setEmail("");
      loadPremiumUsers();
    } catch {
      toast.error("Failed to grant premium");
    } finally {
      setGranting(false);
    }
  };

  const handleRevokePremium = async (userEmail) => {
    if (!window.confirm(`Revoke premium from ${userEmail}?`)) return;
    try {
      await adminRevokePremium(userEmail, passcode);
      toast.success(`Premium revoked from ${userEmail}`);
      loadPremiumUsers();
    } catch {
      toast.error("Failed to revoke premium");
    }
  };

  const handleResetMonthlyReports = async () => {
    if (!window.confirm("Reset this month's report usage to 4/4 for ALL premium users?")) return;
    setResetting(true);
    try {
      const data = await adminResetMonthlyReports(passcode);
      toast.success(`Reset ${data.reset_count} premium user(s) to 4/4 for ${data.month}`);
    } catch {
      toast.error("Failed to reset monthly reports");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Grant Premium */}
      <div
        className="rounded-lg p-6"
        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
        data-testid="premium-grant-section"
      >
        <h3 className="text-lg font-light text-white/90 mb-4 flex items-center gap-2">
          <Crown className="w-5 h-5 text-amber-400" />
          Grant Premium Access
        </h3>
        <p className="text-white/40 text-sm mb-4">
          Manually grant premium status to a user for testing purposes.
        </p>
        <div className="flex gap-3">
          <Input
            type="email"
            placeholder="user@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-white/5 border-white/10 text-white/90 flex-1"
            data-testid="premium-email-input"
          />
          <Button
            onClick={handleGrantPremium}
            disabled={granting || !email.trim()}
            className="bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/30 rounded-full px-6"
            data-testid="premium-grant-btn"
          >
            {granting ? "Granting..." : "Grant Premium"}
            <UserCheck className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Premium Users List */}
      <div
        className="rounded-lg p-6"
        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
        data-testid="premium-users-section"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-light text-white/90 flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-emerald-400" />
            Premium Users ({premiumUsers.length})
          </h3>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResetMonthlyReports}
              disabled={resetting}
              className="text-amber-400/70 hover:text-amber-400 hover:bg-amber-500/10 text-xs"
              data-testid="reset-monthly-reports-btn"
            >
              <RotateCcw className={`w-3.5 h-3.5 mr-1 ${resetting ? "animate-spin" : ""}`} />
              {resetting ? "Resetting..." : "Reset monthly reports"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadPremiumUsers}
              className="text-white/50 hover:text-white text-xs"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loadingUsers ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        </div>
        {premiumUsers.length > 0 ? (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {premiumUsers.map((u, i) => (
              <div
                key={u.email || i}
                className="flex items-center justify-between py-2 px-3 rounded bg-white/[0.02] border border-white/5"
                data-testid={`premium-user-${i}`}
              >
                <div>
                  <p className="text-white/80 text-sm">{u.email}</p>
                  <p className="text-white/30 text-xs">
                    Since: {u.subscribed_at ? new Date(u.subscribed_at).toLocaleDateString() : "N/A"}
                    {u.granted_by && <span className="ml-2 text-amber-400/60">({u.granted_by})</span>}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRevokePremium(u.email)}
                  className="text-red-400/60 hover:text-red-400 hover:bg-red-500/10 text-xs"
                  data-testid={`revoke-btn-${i}`}
                >
                  <UserX className="w-3.5 h-3.5 mr-1" /> Revoke
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-white/30 text-sm">No premium users yet.</p>
        )}
      </div>

      {/* Recent Payments */}
      <div
        className="rounded-lg p-6"
        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
        data-testid="payments-section"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-light text-white/90 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-blue-400" />
            Recent Payments ({payments.length})
          </h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={loadPayments}
            className="text-white/50 hover:text-white text-xs"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loadingPayments ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
        {payments.length > 0 ? (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {payments.map((tx, i) => (
              <div
                key={tx.stripe_session_id || i}
                className="flex items-center justify-between py-2 px-3 rounded bg-white/[0.02] border border-white/5"
                data-testid={`payment-${i}`}
              >
                <div>
                  <p className="text-white/80 text-sm">{tx.package_name || tx.package_id}</p>
                  <p className="text-white/30 text-xs">
                    {tx.metadata?.user_email || "N/A"} • {tx.created_at ? new Date(tx.created_at).toLocaleString() : "N/A"}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-white/70 text-sm">${tx.amount?.toFixed(2)} {tx.currency?.toUpperCase()}</p>
                  <p className={`text-xs ${tx.payment_status === "paid" ? "text-emerald-400" : "text-white/30"}`}>
                    {tx.payment_status}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-white/30 text-sm">No payment transactions yet.</p>
        )}
      </div>
    </div>
  );
}


function KBManagementPanel({ passcode }) {
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [articles, setArticles] = useState([]);
  const [editingArticle, setEditingArticle] = useState(null);
  const [showNewArticle, setShowNewArticle] = useState(false);
  const [loadingCats, setLoadingCats] = useState(true);

  const loadCategories = useCallback(async () => {
    setLoadingCats(true);
    try {
      const data = await fetchKBCategories();
      setCategories(data.categories);
    } catch { toast.error("Failed to load KB categories"); }
    setLoadingCats(false);
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const loadArticles = useCallback(async (slug) => {
    try {
      const data = await fetchKBCategory(slug);
      setArticles(data.articles);
    } catch { toast.error("Failed to load articles"); }
  }, []);

  useEffect(() => {
    if (selectedCat) loadArticles(selectedCat);
  }, [selectedCat, loadArticles]);

  const handleSeedKB = async () => {
    if (!window.confirm("This will reset all KB data to defaults. Continue?")) return;
    try {
      await adminSeedKB(passcode);
      toast.success("Knowledge Base re-seeded");
      loadCategories();
      setSelectedCat(null);
      setArticles([]);
    } catch { toast.error("Seed failed"); }
  };

  const handleDeleteArticle = async (slug) => {
    if (!window.confirm(`Delete article "${slug}"?`)) return;
    try {
      await adminDeleteKBArticle(slug, passcode);
      toast.success("Article deleted");
      if (selectedCat) loadArticles(selectedCat);
      loadCategories();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-kb-panel">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl text-white/90 font-light">Knowledge Base</h2>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" className="text-white/40 hover:text-white text-xs" onClick={handleSeedKB} data-testid="kb-seed-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Re-seed KB
          </Button>
          <Button size="sm" className="bg-white/10 text-white/80 hover:bg-white/15 text-xs" onClick={() => { setShowNewArticle(true); setEditingArticle(null); }} data-testid="kb-new-article-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> New Article
          </Button>
        </div>
      </div>

      {/* Category selector */}
      <div className="flex gap-2 flex-wrap mb-6" data-testid="kb-category-tabs">
        {categories.map((cat) => (
          <button
            key={cat.slug}
            onClick={() => setSelectedCat(cat.slug)}
            className={`px-3 py-1.5 rounded-full text-xs transition-colors ${selectedCat === cat.slug ? "bg-white/15 text-white" : "bg-white/5 text-white/40 hover:text-white/70"}`}
            data-testid={`kb-cat-tab-${cat.slug}`}
          >
            {cat.title} ({cat.article_count})
          </button>
        ))}
      </div>

      {/* Articles list */}
      {selectedCat && (
        <div className="space-y-2" data-testid="kb-articles-list">
          {articles.length === 0 ? (
            <p className="text-white/30 text-sm">No articles in this category.</p>
          ) : articles.map((art) => (
            <div
              key={art.slug}
              className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.03] border border-white/6 hover:border-white/12 transition-colors"
              data-testid={`kb-article-row-${art.slug}`}
            >
              <div className="w-6 h-6 rounded flex items-center justify-center bg-white/5 text-white/25 text-xs shrink-0">{art.order}</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white/80 truncate">{art.title}</p>
                <p className="text-xs text-white/30 truncate">{art.subtitle}</p>
              </div>
              <Button size="sm" variant="ghost" className="text-white/30 hover:text-white h-7 w-7 p-0" onClick={() => { setEditingArticle(art); setShowNewArticle(false); }} data-testid={`kb-edit-${art.slug}`}>
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="ghost" className="text-red-400/50 hover:text-red-400 h-7 w-7 p-0" onClick={() => handleDeleteArticle(art.slug)} data-testid={`kb-delete-${art.slug}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Edit / Create Article Dialog */}
      {(editingArticle || showNewArticle) && (
        <KBArticleEditor
          article={editingArticle}
          categories={categories}
          defaultCategorySlug={selectedCat}
          passcode={passcode}
          onClose={() => { setEditingArticle(null); setShowNewArticle(false); }}
          onSaved={() => {
            setEditingArticle(null);
            setShowNewArticle(false);
            if (selectedCat) loadArticles(selectedCat);
            loadCategories();
          }}
        />
      )}
    </div>
  );
}


function KBArticleEditor({ article, categories, defaultCategorySlug, passcode, onClose, onSaved }) {
  const isNew = !article;
  const [form, setForm] = useState({
    title: article?.title || "",
    subtitle: article?.subtitle || "",
    slug: article?.slug || "",
    category_slug: article?.category_slug || defaultCategorySlug || "",
    order: article?.order || 99,
    keywords: (article?.keywords || []).join(", "),
    content_html: article?.content_html || "",
    related_slugs: (article?.related_slugs || []).join(", "),
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.title || !form.slug || !form.category_slug) {
      toast.error("Title, slug, and category are required");
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title,
      subtitle: form.subtitle,
      slug: form.slug,
      category_slug: form.category_slug,
      order: parseInt(form.order) || 99,
      keywords: form.keywords.split(",").map((k) => k.trim()).filter(Boolean),
      content_html: form.content_html,
      related_slugs: form.related_slugs.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (isNew) {
        await adminCreateKBArticle(payload, passcode);
        toast.success("Article created");
      } else {
        await adminUpdateKBArticle(article.slug, payload, passcode);
        toast.success("Article updated");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-white/90 font-light">{isNew ? "New Article" : `Edit: ${article.title}`}</DialogTitle>
        </DialogHeader>
        <ScrollArea className="flex-1 pr-4">
          <div className="space-y-4 pb-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/50 text-xs">Title *</Label>
                <Input value={form.title} onChange={(e) => updateField("title", e.target.value)} className="bg-transparent border-white/15 text-white/90 mt-1" data-testid="kb-edit-title" />
              </div>
              <div>
                <Label className="text-white/50 text-xs">Slug *</Label>
                <Input value={form.slug} onChange={(e) => updateField("slug", e.target.value)} className="bg-transparent border-white/15 text-white/90 mt-1" data-testid="kb-edit-slug" disabled={!isNew} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/50 text-xs">Category *</Label>
                <select value={form.category_slug} onChange={(e) => updateField("category_slug", e.target.value)} className="w-full mt-1 h-9 rounded-md border border-white/15 bg-transparent text-white/90 text-sm px-3" data-testid="kb-edit-category">
                  <option value="">Select...</option>
                  {categories.map((c) => <option key={c.slug} value={c.slug}>{c.title}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-white/50 text-xs">Order</Label>
                <Input type="number" value={form.order} onChange={(e) => updateField("order", e.target.value)} className="bg-transparent border-white/15 text-white/90 mt-1" data-testid="kb-edit-order" />
              </div>
            </div>
            <div>
              <Label className="text-white/50 text-xs">Subtitle</Label>
              <Input value={form.subtitle} onChange={(e) => updateField("subtitle", e.target.value)} className="bg-transparent border-white/15 text-white/90 mt-1" data-testid="kb-edit-subtitle" />
            </div>
            <div>
              <Label className="text-white/50 text-xs">Keywords (comma-separated)</Label>
              <Input value={form.keywords} onChange={(e) => updateField("keywords", e.target.value)} className="bg-transparent border-white/15 text-white/90 mt-1" data-testid="kb-edit-keywords" />
            </div>
            <div>
              <Label className="text-white/50 text-xs">Content (HTML)</Label>
              <Textarea value={form.content_html} onChange={(e) => updateField("content_html", e.target.value)} rows={12} className="bg-transparent border-white/15 text-white/90 mt-1 font-mono text-xs" data-testid="kb-edit-content" />
            </div>
            <div>
              <Label className="text-white/50 text-xs">Related Article Slugs (comma-separated)</Label>
              <Input value={form.related_slugs} onChange={(e) => updateField("related_slugs", e.target.value)} className="bg-transparent border-white/15 text-white/90 mt-1" data-testid="kb-edit-related" />
            </div>
          </div>
        </ScrollArea>
        <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
          <Button variant="ghost" className="text-white/50" onClick={onClose}>Cancel</Button>
          <Button className="bg-white text-black hover:bg-white/90" onClick={handleSave} disabled={saving} data-testid="kb-save-btn">
            {saving ? "Saving..." : isNew ? "Create" : "Update"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function DigestPanel({ passcode }) {
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [busy, setBusy] = useState(null); // "preview" | "test" | "send"
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewMeta, setPreviewMeta] = useState(null);

  const loadLogs = useCallback(async () => {
    setLoadingLogs(true);
    try {
      const data = await adminDigestLogs(passcode);
      setLogs(data.logs || data || []);
    } catch {
      toast.error("Failed to load digest logs");
    } finally {
      setLoadingLogs(false);
    }
  }, [passcode]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const handlePreview = async () => {
    setBusy("preview");
    try {
      const data = await adminDigestPreview(passcode);
      setPreviewHtml(data.html);
      setPreviewMeta({ week: data.week_label, count: data.event_count });
    } catch {
      toast.error("Preview failed");
    } finally { setBusy(null); }
  };

  const handleTest = async () => {
    if (!testEmail.trim()) { toast.error("Enter a test email"); return; }
    setBusy("test");
    try {
      const data = await adminDigestSendTest(passcode, testEmail.trim());
      if (data.result?.success || data.success) toast.success(`Test digest sent to ${testEmail}`);
      else toast.error(`Send failed: ${data.result?.error || "unknown error"}`);
      loadLogs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed");
    } finally { setBusy(null); }
  };

  const handleSendNow = async () => {
    if (!window.confirm("Send the weekly digest to ALL active subscribers now?")) return;
    setBusy("send");
    try {
      const data = await adminDigestSend(passcode);
      if (data.success === false && data.reason) {
        toast.warning(`Not sent: ${data.reason}`);
      } else if (data.sent_count !== undefined) {
        toast.success(`Digest sent to ${data.sent_count} subscriber(s), ${data.failed_count || 0} failed`);
      } else {
        toast.success("Digest processed");
      }
      loadLogs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="space-y-6" data-testid="admin-digest-panel">
      <div className="rounded-lg p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
        <p className="text-white/80 text-sm font-medium mb-1">Weekly Celestial Digest</p>
        <p className="text-white/40 text-xs leading-6">
          The digest is sent automatically every <span className="text-white/60">Wednesday at 1:30 PM Vancouver time</span> by the
          platform scheduler. Use the controls below to preview it, send yourself a test, or trigger a manual send to all subscribers.
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        <Button onClick={handlePreview} disabled={busy} variant="ghost" className="text-white/70 hover:text-white bg-white/5 hover:bg-white/10 text-sm" data-testid="digest-preview-btn">
          {busy === "preview" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />} Preview
        </Button>
        <Button onClick={handleSendNow} disabled={busy} className="bg-amber-500/90 text-black hover:bg-amber-400 text-sm" data-testid="digest-send-now-btn">
          {busy === "send" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Send Now (all subscribers)
        </Button>
      </div>

      <div className="rounded-lg p-4 flex flex-wrap items-end gap-3" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
        <div className="flex-1 min-w-[220px]">
          <Label className="text-white/50 text-xs">Send a test to a single email</Label>
          <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@example.com" className="bg-black/30 border-white/10 text-white placeholder:text-white/25 mt-1" data-testid="digest-test-email" />
        </div>
        <Button onClick={handleTest} disabled={busy} variant="ghost" className="text-white/70 hover:text-white bg-white/5 hover:bg-white/10 text-sm" data-testid="digest-send-test-btn">
          {busy === "test" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />} Send Test
        </Button>
      </div>

      {previewHtml && (
        <div className="rounded-lg overflow-hidden" style={{ border: "1px solid rgba(255,255,255,0.1)" }} data-testid="digest-preview-box">
          <div className="flex items-center justify-between px-4 py-2 bg-white/5">
            <p className="text-white/60 text-xs">Preview — {previewMeta?.week} · {previewMeta?.count} event(s)</p>
            <button onClick={() => setPreviewHtml(null)} className="text-white/40 hover:text-white/80"><X className="w-4 h-4" /></button>
          </div>
          <iframe title="digest-preview" srcDoc={previewHtml} className="w-full bg-white" style={{ height: 500 }} />
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-white/70 text-sm font-medium">Recent sends</p>
          <Button onClick={loadLogs} variant="ghost" size="sm" className="text-white/40 hover:text-white text-xs" data-testid="digest-refresh-logs-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>
        {loadingLogs ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-white/40 animate-spin" /></div>
        ) : logs.length === 0 ? (
          <p className="text-white/30 text-sm py-4">No digest sends logged yet.</p>
        ) : (
          <div className="space-y-2" data-testid="digest-logs-list">
            {logs.map((log, i) => (
              <div key={log.id || i} className="rounded-lg p-3 flex items-center justify-between text-sm" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}>
                <div>
                  <span className="text-white/70">{log.week_label || log.subject || log.type}</span>
                  <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-white/5 text-white/40">{log.type}</span>
                  {log.email && <span className="ml-2 text-white/40 text-xs">{log.email}</span>}
                </div>
                <div className="text-right text-xs text-white/40">
                  {log.sent_count !== undefined && <span className="text-emerald-300/70">{log.sent_count} sent{log.failed_count ? `, ${log.failed_count} failed` : ""} · </span>}
                  {log.sent_at ? new Date(log.sent_at).toLocaleString() : ""}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
